import React, { useRef, useState, useMemo } from "react";
import { Play, Pause } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmt = (s) => (isFinite(s) ? `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}` : "0:00");

// Native-feeling voice note: stylized waveform, scrub, playback speed.
export const VoiceNote = ({ src, mine }) => {
  const url = src?.startsWith("http") ? src : `${BACKEND}${src}`;
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const [rate, setRate] = useState(1);

  const bars = useMemo(() => {
    let h = 0;
    for (const c of (src || "x")) h = (h * 31 + c.charCodeAt(0)) >>> 0;
    return Array.from({ length: 34 }, (_, i) => 30 + ((h >> (i % 24)) ^ (i * 53)) % 70);
  }, [src]);

  const toggle = () => {
    const a = audioRef.current; if (!a) return;
    if (playing) a.pause(); else { a.playbackRate = rate; a.play(); }
  };
  const cycleRate = () => {
    const next = rate === 1 ? 1.5 : rate === 1.5 ? 2 : 1;
    setRate(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };
  const progress = dur ? cur / dur : 0;
  const filled = Math.floor(progress * bars.length);
  const seek = (e) => {
    const a = audioRef.current; if (!a || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    a.currentTime = ((e.clientX - rect.left) / rect.width) * dur;
  };

  return (
    <div data-testid="voice-note" className="mt-1 flex w-60 items-center gap-2">
      <audio ref={audioRef} src={url} preload="metadata"
        onLoadedMetadata={(e) => setDur(e.target.duration || 0)}
        onTimeUpdate={(e) => setCur(e.target.currentTime)}
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setCur(0); }} />
      <button data-testid="voice-toggle" onClick={toggle}
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${mine ? "bg-white/25 text-white" : "bg-primary/15 text-primary"}`}>
        {playing ? <Pause size={16} weight="fill" /> : <Play size={16} weight="fill" />}
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex h-8 cursor-pointer items-center gap-[2px]" onClick={seek}>
          {bars.map((b, i) => (
            <span key={i} style={{ height: `${b}%` }}
              className={`w-[3px] rounded-full ${i <= filled ? (mine ? "bg-white" : "bg-primary") : (mine ? "bg-white/35" : "bg-primary/25")}`} />
          ))}
        </div>
        <div className={`mt-0.5 flex items-center justify-between text-[10px] ${mine ? "text-white/70" : "text-muted-foreground"}`}>
          <span>{fmt(playing || cur ? cur : dur)}</span>
          <button data-testid="voice-speed" onClick={cycleRate} className="rounded-full bg-black/10 px-1.5 font-semibold">{rate}x</button>
        </div>
      </div>
    </div>
  );
};

export default VoiceNote;
